import { useState, useMemo, useCallback, useRef } from "react";

/**
 * 节点/边/选中态/相位管理
 * @param {Array} initialNodes
 * @param {Array} initialEdges
 */
export function useFlowState(initialNodes, initialEdges) {
  const [allNodes, setAllNodes] = useState(initialNodes);
  const [allEdges, setAllEdges] = useState(initialEdges);
  const [activePhase, setActivePhase] = useState("main");
  const [selectedId, setSelectedId] = useState(null);

  // 自增位置计数器：避免新节点重叠在原点
  const addCounter = useRef(0);

  const visibleNodes = useMemo(
    () => allNodes.filter((n) => n.phase === activePhase),
    [allNodes, activePhase]
  );

  const visibleEdges = useMemo(
    () =>
      allEdges.filter(
        (e) =>
          visibleNodes.some((n) => n.id === e.source) &&
          visibleNodes.some((n) => n.id === e.target)
      ),
    [allEdges, visibleNodes]
  );

  const selectedNode = useMemo(
    () => (selectedId ? allNodes.find((n) => n.id === selectedId) ?? null : null),
    [selectedId, allNodes]
  );

  const addNode = useCallback(
    (action, defaults, position = null, extras = null) => {
      const id = `node-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      // 默认位置：点击添加通常已由上层注入视口中心；此处仅作双重保险（原点附近错开）
      addCounter.current += 1;
      const fallbackPos = {
        x: 60 + ((addCounter.current * 40) % 240),
        y: 60 + ((addCounter.current * 30) % 200),
      };
      const raw = defaults?.componentData;
      const isSingleInput = extras?.isSingleInput === true;
      let componentData;
      if (isSingleInput) {
        // single_input：保留原生标量 / null（int/float/bool/object 都不归一成 dict）
        componentData = raw === undefined ? null : raw;
      } else if (raw === undefined || raw === null) {
        componentData = {};
      } else if (typeof raw === "object") {
        componentData = structuredClone(raw);
      } else {
        componentData = String(raw);
      }
      const newNode = {
        id,
        phase: activePhase,
        action,
        position: position ?? fallbackPos,
        componentData,
        ...(extras ?? {}),
      };
      setAllNodes((nds) => [...nds, newNode]);
      // 返回新节点 id：供上层做"刚添加节点"的精确竞态防护（忽略针对该 id 的误发 remove）
      return id;
    },
    [activePhase]
  );

  const updateNodeData = useCallback(
    (id, partial) => {
      setAllNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, ...partial } : n))
      );
    },
    []
  );

  const deleteNode = useCallback((id) => {
    setAllNodes((nds) => nds.filter((n) => n.id !== id));
    setAllEdges((eds) =>
      eds.filter((e) => e.source !== id && e.target !== id)
    );
    setSelectedId(null);
  }, []);

  return {
    allNodes,
    allEdges,
    setAllNodes,
    setAllEdges,
    visibleNodes,
    visibleEdges,
    activePhase,
    setActivePhase,
    selectedId,
    setSelected: setSelectedId,
    selectedNode,
    addNode,
    updateNodeData,
    deleteNode,
  };
}
